/**
 * Club operating hours helpers for court hire availability.
 */

import { formatInTimeZone } from 'date-fns-tz';
import type { ClubClosure, ClubOperatingHour } from '../types/organization';
import { timeStringToMinutes } from './timeOverlap';

export const DEFAULT_CLUB_OPERATING_START = '06:00';
export const DEFAULT_CLUB_OPERATING_END = '22:00';

export interface ClubOperatingHourSlot {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

/** Sun–Sat 06:00–22:00 when no custom hours are stored. */
export function getDefaultClubOperatingHours(): ClubOperatingHourSlot[] {
  return Array.from({ length: 7 }, (_, dayOfWeek) => ({
    dayOfWeek,
    startTime: DEFAULT_CLUB_OPERATING_START,
    endTime: DEFAULT_CLUB_OPERATING_END,
  }));
}

export function normalizeClubTimeHm(time: string): string {
  const trimmed = time.trim();
  if (trimmed.length >= 5) return trimmed.slice(0, 5);
  return trimmed;
}

export function resolveEffectiveClubOperatingHours(
  rows: ClubOperatingHour[]
): ClubOperatingHourSlot[] {
  if (rows.length === 0) return getDefaultClubOperatingHours();
  return rows.map((row) => ({
    dayOfWeek: row.dayOfWeek,
    startTime: normalizeClubTimeHm(row.startTime),
    endTime: normalizeClubTimeHm(row.endTime),
  }));
}

function calendarDayOfWeekFromYmd(ymd: string): number {
  const parts = ymd.split('-').map(Number);
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    const [y, m, d] = parts;
    return new Date(y, m - 1, d).getDay();
  }
  return new Date(ymd).getUTCDay();
}

function clubLocalParts(date: Date, timeZone: string): { dateYmd: string; timeHm: string } {
  return {
    dateYmd: formatInTimeZone(date, timeZone, 'yyyy-MM-dd'),
    timeHm: formatInTimeZone(date, timeZone, 'HH:mm'),
  };
}

/**
 * True when the booking interval lies entirely within the club's operating window
 * for the club-local calendar day of the start time.
 */
export function isBookingWithinClubOperatingHours(
  timeZone: string,
  operatingHours: ClubOperatingHourSlot[],
  startAt: Date,
  endAt: Date
): boolean {
  if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime()) || endAt <= startAt) {
    return false;
  }

  const startParts = clubLocalParts(startAt, timeZone);
  const endParts = clubLocalParts(endAt, timeZone);

  if (startParts.dateYmd !== endParts.dateYmd) {
    return false;
  }

  const dayOfWeek = calendarDayOfWeekFromYmd(startParts.dateYmd);
  const window = operatingHours.find((h) => h.dayOfWeek === dayOfWeek);
  if (!window) return false;

  const windowStart = timeStringToMinutes(normalizeClubTimeHm(window.startTime));
  const windowEnd = timeStringToMinutes(normalizeClubTimeHm(window.endTime));
  const bookingStart = timeStringToMinutes(startParts.timeHm);
  const bookingEnd = timeStringToMinutes(endParts.timeHm);

  return bookingStart >= windowStart && bookingEnd <= windowEnd;
}

/** True when the booking overlaps any club closure interval. */
export function doesBookingOverlapClubClosure(
  closures: ClubClosure[],
  startAt: Date,
  endAt: Date
): boolean {
  const startMs = startAt.getTime();
  const endMs = endAt.getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return true;

  return closures.some((closure) => {
    const closureStart = closure.startAt.getTime();
    const closureEnd = closure.endAt.getTime();
    return startMs < closureEnd && endMs > closureStart;
  });
}

export function validateClubOperatingHourSlot(slot: ClubOperatingHourSlot): string | null {
  const start = normalizeClubTimeHm(slot.startTime);
  const end = normalizeClubTimeHm(slot.endTime);
  if (slot.dayOfWeek < 0 || slot.dayOfWeek > 6) return 'dayOfWeek must be between 0 and 6';
  if (timeStringToMinutes(end) <= timeStringToMinutes(start)) {
    return 'endTime must be after startTime';
  }
  return null;
}
